<template>
  <div
    class="party-screen"
    :class="[
      'phase-' + (snap.phase || 'IDLE').toLowerCase(),
      { 'has-scene-bg': !!sceneBgUrl }
    ]"
    :style="rootBgStyle"
  >
    <div v-if="bootError" class="party-screen__error">{{ bootError }}</div>
    <template v-else>
      <div v-if="sceneBgUrl" class="party-screen__bg" :style="sceneBgLayerStyle" aria-hidden="true" />
      <div v-if="sceneBgUrl" class="party-screen__veil" aria-hidden="true" />
      <div
        v-if="onlineOverlayStyle"
        class="party-screen__online"
        :style="onlineOverlayStyle"
      >已入场 {{ joinedCount }} 人</div>
      <transition name="party-fade">
        <div :key="snap.screenSceneKey" class="party-screen__stage" :class="{ 'is-fx-stage': isFxStage }">
          <sound-wave-stage
            v-if="isSoundWave"
            :config="soundWaveConfig"
            :active="soundWaveActive"
          />

          <!-- 首页 / 介绍：纯背景（文案请在背景编辑器中写入） -->
          <div v-else-if="isHome || isIntro" class="scene scene-display" />

          <!-- 导航 -->
          <div v-else-if="isNav" class="scene scene-nav">
            <h1 class="scene-title">选择互动</h1>
            <div class="nav-cards">
              <div v-for="g in enabledGames" :key="g.key" class="nav-card">{{ g.label || g.key }}</div>
            </div>
            <p class="scene-sub">已入场 {{ joinedCount }}</p>
          </div>

          <!-- 跑酷预热 -->
          <div v-else-if="isWarmup" class="scene scene-warmup">
            <h1 class="scene-title">{{ scoreLabel }} · 准备中</h1>
            <p class="scene-sub">观众端已锁定 · 等待开始</p>
            <p class="scene-tip">已入场 {{ joinedCount }} 人</p>
          </div>

          <!-- 倒计时 / 进行 / 结算 -->
          <div v-else-if="isPlay" class="scene scene-play">
            <div class="score-banner">{{ snap.phase === 'SETTLED' ? '本局结果' : playTitle }}</div>
            <div v-if="snap.phase === 'COUNTDOWN'" class="countdown">{{ countdownLeft }}</div>
            <template v-else>
              <div v-if="podiumList.length" class="podium">
                <div
                  v-for="(p, i) in podiumList"
                  :key="p.uid"
                  class="podium-item"
                  :class="'is-no' + (i + 1)"
                >
                  <span class="podium-medal">{{ i + 1 }}</span>
                  <img v-if="p.avatar" class="podium-avatar" :src="p.avatar" alt="" />
                  <span v-else class="podium-avatar is-text">{{ (p.nick || p.uid || '?').slice(0, 1) }}</span>
                  <span class="podium-nick">{{ p.nick || p.uid }}</span>
                  <span class="podium-score">{{ p.score }}</span>
                </div>
              </div>
              <div v-if="restList.length" class="rank-board">
                <div v-for="p in restList" :key="p.uid" class="rank-item">
                  <span class="rank-no">{{ p.rank }}</span>
                  <img v-if="p.avatar" class="rank-avatar" :src="p.avatar" alt="" />
                  <span class="rank-nick">{{ p.nick || p.uid }}</span>
                  <span class="rank-score">{{ p.score }}</span>
                </div>
              </div>
              <div v-if="!topList.length" class="rank-empty">等待观众开始游戏…</div>
            </template>
          </div>

          <lottery-stage
            v-else-if="stageKind === 'lottery' || stageKind === 'lottery_variant'"
            :guests="lotteryGuests"
            :draft="lotteryDraft"
            :h5="lotteryH5"
            :online-count="joinedCount"
            :label="currentGameLabel || '昵称抽奖'"
          />

          <photo-lottery-stage
            v-else-if="stageKind === 'photo_lottery'"
            :photos="lotteryPhotos"
            :draft="lotteryDraft"
            :h5="lotteryH5"
            :online-count="joinedCount"
          />

          <rolling-avatar-fx
            v-else-if="stageKind === 'rolling_avatar'"
            :guests="lotteryGuests"
            :draft="lotteryDraft"
            :h5="lotteryH5"
            :online-count="joinedCount"
            :label="currentGameLabel || '头像滚动抽奖'"
          />

          <open-box-fx
            v-else-if="stageKind === 'open_box'"
            :guests="lotteryGuests"
            :draft="lotteryDraft"
            :h5="lotteryH5"
            :online-count="joinedCount"
            :label="currentGameLabel || '开盲盒抽奖'"
          />

          <message-wall-screen
            v-else-if="stageKind === 'message'"
            :game-data="gameData"
            :online-count="joinedCount"
            :title="currentGameLabel"
          />

          <vote-screen
            v-else-if="stageKind === 'vote'"
            :game-data="gameData"
            :online-count="joinedCount"
            :title="currentGameLabel"
          />

          <red-packet-screen
            v-else-if="stageKind === 'red_packet'"
            :game-data="gameData"
            :online-count="joinedCount"
            :title="currentGameLabel"
          />

          <signin-wall-screen
            v-else-if="stageKind === 'signin_wall'"
            :guests="wallGuests"
            :online-count="joinedCount"
            :title="currentGameLabel"
          />

          <div v-else class="scene scene-display" />
        </div>
      </transition>

      <!-- 简易粒子层：切换时闪一下（完整粒子可后续增强） -->
      <div v-if="showBurst" class="particle-burst" aria-hidden="true" />
    </template>
  </div>
</template>

<script>
import { partyScreenBootstrap, partySnapshot } from '@/api/party'
import { getImageUrl } from '@/utils/image'
import { initPartyTrack, disposePartyTrack, setPartyTrackContext, track } from '@/utils/party-track'
import LotteryStage from './lotteryStage.vue'
import PhotoLotteryStage from './PhotoLotteryStage.vue'
import SoundWaveStage from './SoundWaveStage.vue'
import RollingAvatarFx from './games/RollingAvatarFx.vue'
import OpenBoxFx from './games/OpenBoxFx.vue'
import MessageWallScreen from './games/MessageWallScreen.vue'
import VoteScreen from './games/VoteScreen.vue'
import RedPacketScreen from './games/RedPacketScreen.vue'
import SigninWallScreen from './games/SigninWallScreen.vue'
import { resolveH5StageKind, resolveStageKindByGame, isFxStageKind, isScoreStageKind } from './registry'

export default {
  name: 'PartyScreen',
  components: {
    LotteryStage,
    PhotoLotteryStage,
    SoundWaveStage,
    RollingAvatarFx,
    OpenBoxFx,
    MessageWallScreen,
    VoteScreen,
    RedPacketScreen,
    SigninWallScreen
  },
  data() {
    return {
      bootError: '',
      activityCode: '',
      signed: false,
      snap: {
        screenSceneKey: 'home',
        phase: 'IDLE',
        onlineCount: 0,
        participantCount: 0,
        topPlayers: [],
        partyConfig: {},
        countdownEndAt: 0,
        rev: 0
      },
      lastRev: 0,
      lastTransitionId: '',
      showBurst: false,
      pollTimer: null,
      tickTimer: null,
      burstTimer: null,
      polling: false,
      now: Date.now(),
    }
  },
  computed: {
    scenes() {
      return (this.snap.partyConfig && this.snap.partyConfig.scenes) || []
    },
    currentScene() {
      return this.scenes.find(s => s.sceneKey === this.snap.screenSceneKey) || null
    },
    enabledGames() {
      const games = (this.snap.partyConfig && this.snap.partyConfig.games) || []
      return games.filter(g => g.enabled !== false)
    },
    isHome() {
      return this.snap.screenSceneKey === 'home'
    },
    isIntro() {
      return String(this.snap.screenSceneKey || '').startsWith('intro.')
    },
    isNav() {
      return this.snap.screenSceneKey === 'nav'
    },
    stageKind() {
      return resolveH5StageKind({
        sceneKey: this.snap.screenSceneKey,
        scene: this.currentScene,
        phase: this.snap.phase,
        partyConfig: this.snap.partyConfig
      })
    },
    isFxStage() {
      return isFxStageKind(this.stageKind)
    },
    isWarmup() {
      const key = String(this.snap.screenSceneKey || '')
      if (key.endsWith('.warmup')) return true
      return isScoreStageKind(this.stageKind) && !key.endsWith('.play') && this.snap.phase === 'WARMUP'
    },
    isPlay() {
      const key = String(this.snap.screenSceneKey || '')
      if (key.endsWith('.play')) return true
      return isScoreStageKind(this.stageKind) && !key.endsWith('.warmup') &&
        ['COUNTDOWN', 'RUNNING', 'SETTLED'].indexOf(this.snap.phase) >= 0
    },
    isSoundWave() {
      return this.stageKind === 'sound_wave'
    },
    scoreGameKey() {
      const scene = this.currentScene
      if (scene && scene.gameKey && resolveStageKindByGame(scene.gameKey) === 'score') return scene.gameKey
      const key = String(this.snap.screenSceneKey || '')
      const games = (this.snap.partyConfig && this.snap.partyConfig.games) || []
      const byPrefix = games.find(g => g && g.key && key.indexOf(g.key) >= 0 && resolveStageKindByGame(g.key) === 'score')
      if (byPrefix) return byPrefix.key
      const enabled = games.find(g => g && g.enabled !== false && g.key && resolveStageKindByGame(g.key) === 'score')
      return (enabled && enabled.key) || 'tap_race'
    },
    /** 当前场景玩法中文名（用于消息/投票/红包/展示舞台标题） */
    currentGameLabel() {
      const scene = this.currentScene
      if (scene && scene.gameKey) {
        const games = (this.snap.partyConfig && this.snap.partyConfig.games) || []
        const g = games.find(x => x && x.key === scene.gameKey)
        if (g && g.label) return g.label
      }
      return ''
    },
    /** 实时玩法数据：消息 / 投票 / 红包 */
    gameData() {
      return this.snap.gameData || {}
    },
    scoreLabel() {
      if (this.scoreGameKey === 'money') return '疯狂数钱'
      return this.currentGameLabel || '点击跑酷'
    },
    playTitle() {
      if (this.scoreGameKey === 'money') return '疯狂数钱中'
      return (this.currentGameLabel || '激烈竞速') + '中'
    },
    soundWaveConfig() {
      return (this.snap.partyConfig && this.snap.partyConfig.soundWave) || {}
    },
    soundWaveActive() {
      if (this.soundWaveConfig.controlRequired !== true) {
        return this.snap.phase !== 'SETTLED'
      }
      return this.snap.phase === 'RUNNING' || this.snap.phase === 'COUNTDOWN'
    },
    lotteryDraft() {
      return this.snap.lotteryDraft || null
    },
    /** 照片抽奖素材：后端只返回已上传照片的观众 */
    lotteryPhotos() {
      return this.snap.photos || []
    },
    /** 已入场人数：入场名单，非实时在线 */
    joinedCount() {
      if (this.snap.participantCount != null && this.snap.participantCount !== '') {
        return Number(this.snap.participantCount) || 0
      }
      return Number(this.snap.onlineCount) || 0
    },
    lotteryH5() {
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      return h5
    },
    /** 抽奖/动效类舞台不铺场景背景图；首页/介绍/导航本身就是纯背景舞台，玩法兜底解析成动效时仍要铺 */
    sceneBgUrl() {
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      const raw = this.isSoundWave ? (this.soundWaveConfig.background || '') : (h5.bg || '')
      if (!raw) return ''
      const plainDisplay = this.isHome || this.isIntro || this.isNav
      if (this.isFxStage && !plainDisplay) return ''
      return getImageUrl(raw)
    },
    sceneBgLayerStyle() {
      if (!this.sceneBgUrl) return {}
      return {
        backgroundImage: 'url(' + this.sceneBgUrl + ')'
      }
    },
    /** 背景编辑器配置的入场人数叠层（1920×1080 坐标 → 百分比） */
    onlineOverlayStyle() {
      const h5 = (this.currentScene && this.currentScene.h5) || {}
      const o = h5.onlineOverlay
      if (!o || typeof o !== 'object') return null
      const fontSize = Math.max(16, Number(o.fontSize) || 36)
      return {
        left: ((Number(o.x) || 0) / 1920 * 100) + '%',
        top: ((Number(o.y) || 0) / 1080 * 100) + '%',
        fontSize: 'clamp(14px, ' + (fontSize / 1920 * 100) + 'vw, ' + fontSize + 'px)',
        color: o.color || '#ffffff'
      }
    },
    rootBgStyle() {
      // 有图时去掉默认渐变，避免盖住背景层
      if (this.sceneBgUrl) {
        return { background: '#0b1020' }
      }
      return {}
    },
    lotteryGuests() {
      // 抽奖球名单：优先有昵称的入场玩家（后端 topPlayers 含仅入场无积分）
      const list = this.snap.topPlayers || []
      return list.filter(g => g && (g.nick || g.nickName || g.uid))
    },
    topList() {
      return (this.snap.topPlayers || []).slice(0, 20)
    },
    /** 计分舞台前三领奖台 */
    podiumList() {
      return this.topList.slice(0, 3)
    },
    restList() {
      return this.topList.slice(3)
    },
    /** 签到墙最多展示 56 人，避免 80 人时超出大屏可视区 */
    wallGuests() {
      return this.lotteryGuests.slice(0, 56)
    },
    countdownLeft() {
      const end = Number(this.snap.countdownEndAt || 0)
      if (!end || this.snap.phase !== 'COUNTDOWN') return 0
      return Math.max(0, Math.ceil((end - this.now) / 1000))
    }
  },
  async mounted() {
    const q = this.$route.query || {}
    this.activityCode = String(q.activity_code || q.activityCode || '').trim()
    if (!this.activityCode) {
      this.bootError = '缺少 activity_code'
      return
    }
    try {
      if (q.sign && q.ts) {
        const res = await partyScreenBootstrap({
          activity_code: this.activityCode,
          store_code: q.store_code || undefined,
          ts: q.ts,
          sign: q.sign
        })
        this.applySnap(this.unwrap(res), true)
        this.signed = true
      } else {
        const res = await partySnapshot({ activityCode: this.activityCode })
        this.applySnap(this.unwrap(res), true)
      }
    } catch (e) {
      this.bootError = (e && e.message) || '大屏鉴权/加载失败'
      return
    }
    this.startLoops()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    setPartyTrackContext({
      activityCode: this.activityCode,
      storeCode: q.store_code || '',
      openid: 'screen:' + this.activityCode
    })
    initPartyTrack()
    track('party_screen_enter', { signed: !!q.sign })
  },
  beforeDestroy() {
    this.stopLoops()
    clearTimeout(this.burstTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    disposePartyTrack()
  },
  methods: {
    unwrap(res) {
      if (!res) return null
      if (res.data != null && (res.success === true || res.status === 200 || res.code === 200 || res.ok)) {
        return res.data
      }
      if (res.screenSceneKey || res.phase) return res
      return res.data || res
    },
    applySnap(data, first) {
      if (!data || typeof data !== 'object') return
      const rev = Number(data.rev || 0)
      // rev 未变则跳过整页重渲染（抽奖球不被 800ms 轮询打断）
      if (!first && rev && rev === this.lastRev) {
        let countChanged = false
        if (data.participantCount != null && data.participantCount !== this.snap.participantCount) {
          this.$set(this.snap, 'participantCount', data.participantCount)
          countChanged = true
        }
        if (data.onlineCount != null && data.onlineCount !== this.snap.onlineCount) {
          this.$set(this.snap, 'onlineCount', data.onlineCount)
        }
        // 新入场会改变名单但不会 bump rev，这里只同步名单，避免抽奖球/签到墙滞后
        if (countChanged) {
          if (Array.isArray(data.topPlayers)) this.$set(this.snap, 'topPlayers', data.topPlayers)
          if (Array.isArray(data.photos)) this.$set(this.snap, 'photos', data.photos)
        }
        return
      }
      const prevRev = this.lastRev
      const next = Object.assign({}, this.snap, data)
      if (data.partyConfig) next.partyConfig = data.partyConfig
      if (data.lotteryDraft !== undefined) next.lotteryDraft = data.lotteryDraft
      if (data.screenSceneKey != null) next.screenSceneKey = data.screenSceneKey
      if (data.phase != null) next.phase = data.phase
      this.snap = next
      const tr = data.transition || {}
      const trId = tr.id || ''
      const targets = tr.targets || []
      if (!first && rev && rev !== prevRev && targets.indexOf('screen') >= 0 && trId && trId !== this.lastTransitionId) {
        this.lastTransitionId = trId
        this.burst()
      }
      this.lastRev = rev || this.lastRev
    },
    burst() {
      this.showBurst = true
      clearTimeout(this.burstTimer)
      this.burstTimer = setTimeout(() => {
        this.showBurst = false
      }, 700)
    },
    startLoops() {
      this.stopLoops()
      this.schedulePoll(0)
      // 仅倒计时阶段需要 200ms 刷新，其余阶段不触发无意义重渲染
      this.tickTimer = setInterval(() => {
        if (this.snap.phase !== 'COUNTDOWN') return
        this.now = Date.now()
      }, 200)
    },
    stopLoops() {
      if (this.pollTimer) clearTimeout(this.pollTimer)
      if (this.tickTimer) clearInterval(this.tickTimer)
      this.pollTimer = null
      this.tickTimer = null
    },
    schedulePoll(delay) {
      if (this.bootError) return
      this.pollTimer = setTimeout(() => this.poll(), delay)
    },
    /** 抽奖/比赛进行中加密轮询，待机时降频 */
    nextPollDelay() {
      const phase = this.snap.phase
      if (phase === 'COUNTDOWN' || phase === 'RUNNING' || phase === 'LOTTERY') return 600
      return 1200
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.stopLoops()
        return
      }
      this.startLoops()
    },
    async poll() {
      if (this.bootError) return
      if (this.pollTimer) {
        clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
      // 后台不轮询，回到前台由 visibilitychange 重启
      if (document.hidden) return
      // 避免慢请求叠加，等本次回来后再排下一次
      if (this.polling) return
      this.polling = true
      try {
        const res = await partySnapshot({ activityCode: this.activityCode })
        this.applySnap(this.unwrap(res), false)
      } catch (e) {
        if (!this._pollErrLogged) {
          this._pollErrLogged = true
          console.warn('[party-screen] snapshot poll failed', e)
        }
      } finally {
        this.polling = false
        if (!document.hidden) this.schedulePoll(this.nextPollDelay())
      }
    }
  }
}
</script>

<style scoped>
.party-screen {
  min-height: 100vh;
  background: radial-gradient(ellipse at center, #1a2740 0%, #0b1020 70%);
  color: #f5f7fa;
  overflow: hidden;
  position: relative;
}
.party-screen__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}
.party-screen__online {
  position: absolute;
  z-index: 3;
  font-weight: 600;
  letter-spacing: 1px;
  white-space: nowrap;
  pointer-events: none;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.65);
  transform-origin: left top;
}
.party-screen__veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(8, 12, 24, 0.35) 0%,
    rgba(8, 12, 24, 0.2) 40%,
    rgba(8, 12, 24, 0.45) 100%
  );
  pointer-events: none;
}
.party-screen__error {
  padding: 48px;
  font-size: 24px;
  text-align: center;
  color: #f56c6c;
  position: relative;
  z-index: 2;
}
.party-screen__stage {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  z-index: 2;
}
.party-screen__stage.is-fx-stage {
  padding: 0;
  display: block;
  position: relative;
}
.scene {
  width: 100%;
  max-width: 1100px;
  text-align: center;
}
.party-screen.has-scene-bg .scene-title,
.party-screen.has-scene-bg .scene-sub,
.party-screen.has-scene-bg .scene-tip {
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.65);
}
.scene-title {
  font-size: 56px;
  margin: 0 0 16px;
  letter-spacing: 2px;
}
.scene-sub {
  font-size: 28px;
  opacity: 0.85;
  margin: 8px 0;
}
.scene-tip {
  font-size: 20px;
  opacity: 0.6;
  margin-top: 24px;
}
.nav-cards {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 32px 0;
  flex-wrap: wrap;
}
.nav-card {
  min-width: 180px;
  padding: 28px 24px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  font-size: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.countdown {
  font-size: 180px;
  font-weight: 700;
  line-height: 1;
  text-shadow: 0 0 40px rgba(64, 158, 255, 0.6);
}
.score-banner {
  display: inline-block;
  margin-bottom: 18px;
  padding: 8px 36px;
  border-radius: 24px;
  background: linear-gradient(90deg, #7b3cff, #c84bff);
  font-size: 28px;
  letter-spacing: 4px;
  box-shadow: 0 0 24px rgba(160, 80, 255, 0.55);
}
.podium {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 24px;
  margin: 26px auto 10px;
}
.podium-item {
  width: 200px;
  padding: 18px 14px 16px;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  animation: podiumIn 0.5s ease both;
}
.podium-item.is-no1 {
  background: linear-gradient(180deg, rgba(255, 205, 100, 0.28), rgba(120, 70, 10, 0.18));
  border-color: rgba(255, 214, 102, 0.75);
  box-shadow: 0 0 36px rgba(255, 200, 90, 0.35);
  transform: translateY(-14px);
}
.podium-item.is-no2 {
  border-color: rgba(200, 220, 255, 0.6);
}
.podium-item.is-no3 {
  border-color: rgba(240, 170, 120, 0.6);
}
.podium-medal {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 800;
  color: #0b1020;
  background: linear-gradient(135deg, #ffe9a6, #ffc247);
}
.podium-avatar {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 700;
  background: linear-gradient(135deg, #34558f, #1b2a4a);
}
.podium-nick {
  max-width: 100%;
  font-size: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.podium-score {
  font-size: 30px;
  font-weight: 800;
  color: #ffe6a0;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 18px rgba(255, 210, 100, 0.45);
}
@keyframes podiumIn {
  from { opacity: 0; transform: translateY(18px) scale(0.92); }
  to { opacity: 1; }
}
.rank-empty {
  margin-top: 40px;
  font-size: 24px;
  opacity: 0.6;
}
.rank-board {
  margin: 18px auto 0;
  max-width: 720px;
  text-align: left;
}
.rank-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 16px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  font-size: 22px;
}
.rank-no {
  width: 36px;
  font-weight: 700;
  color: #79bbff;
}
.rank-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.rank-nick {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rank-score {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
.particle-burst {
  pointer-events: none;
  position: fixed;
  inset: 0;
  background:
    radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.35) 0 2px, transparent 3px),
    radial-gradient(circle at 70% 40%, rgba(121, 187, 255, 0.5) 0 2px, transparent 3px),
    radial-gradient(circle at 40% 80%, rgba(255, 200, 100, 0.45) 0 2px, transparent 3px),
    radial-gradient(circle at 85% 70%, rgba(255, 255, 255, 0.3) 0 2px, transparent 3px);
  background-size: 100% 100%;
  animation: burst 0.7s ease-out forwards;
  z-index: 20;
}
@keyframes burst {
  0% { opacity: 0; transform: scale(0.92); }
  30% { opacity: 1; }
  100% { opacity: 0; transform: scale(1.08); }
}
.party-fade-enter-active,
.party-fade-leave-active {
  transition: opacity 0.35s ease;
}
.party-fade-enter,
.party-fade-leave-to {
  opacity: 0;
}
</style>
